import {
  Component, EventEmitter, HostBinding, inject, Input, OnChanges, OnDestroy, OnInit, Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ProjectsService } from '../../../../modules/projects/core/projects.service';
import { projectDetails } from '../../../../modules/projects/core/projects.selectors';
import { AuthService } from '../../../services/auth.service';
import { CommonService } from '../../../services/common.service';
import { WorkflowService } from '../../../services/workflow.service';
import {
  IssueStatusChip,
  IssueWorkflowIssue,
  IssueAssignee,
  IssueWorkflowPerson,
  issueAssignees,
  IssueWorkflowProject,
  ISSUE_WORKFLOW_STATES,
  WorkflowAttachment,
  WorkflowDiscussionEntry,
  WorkflowFormInput,
  WorkflowInputKind,
  WorkflowInputOption,
  WorkflowLineageStep,
  WorkflowNextAction,
  buildWorkflowFormValues,
  isIssueWorkflowEnabled,
  issuePageProjectId,
  issueStatusChip,
  issueWorkflowActionsFor,
  issueWorkflowHistory,
  issueWorkflowId,
  resolveIssueWorkflowProject,
  workflowActionLabel,
  workflowInputKind,
  workflowInputLabel,
  workflowInputOptions,
  workflowPeopleOfSubContractor,
  workflowPersonLabel,
  workflowPersonOptions,
  workflowSubContractorOptions,
} from '../../../helpers/issue-workflow';

/** What the host is told after a workflow action went through. */
export interface IssueWorkflowChange {
  issue: IssueWorkflowIssue;
  state: string;
}

/** One input of the picked action, as the inline form renders it. */
export interface IssueWorkflowActionField {
  input: WorkflowFormInput;
  kind: WorkflowInputKind;
  label: string;
}

interface ApiError {
  error?: { error?: string; message?: string };
}

// The inputs the API leaves open and this panel has the list for
// (GET /v1/issue/:id/contractor-users).
const USERS_INPUT = 'account_ids';
const SUB_CONTRACTOR_INPUT = 'sub_contractor_id';
// Uploads for an action carry this tag (app-pictures-section-with-tags sends
// it, the API returns it): that is what tells them from the issue's own media
// on a screen that shows both. Same tag the punch list actions use.
const ACTION_ATTACHMENT_TAG = 'actions-attachments';

/**
 * The contractor workflow of one issue, wrapped around what the host shows of
 * the issue (its form or read-only fields, passed as content). Sits on every
 * surface that shows one issue.
 *
 * On a project with the workflow switched on (see resolveIssueWorkflowProject)
 * it lays the issue out like the punch list item detail
 * (punchlist-item-detail.component.html), with the same tabs, rows, action
 * area and Discussion:
 *  - Details: the status, the host's content, who the issue is assigned
 *    to (one "Forwarded to" list), then the "actions" pick list
 *    with the picked action's inputs inline and the Submit button;
 *  - Discussion: the history.
 * Anywhere else it is transparent: the host's content is rendered as it is,
 * the element itself takes no box (`display: contents`), there are no tabs
 * and no request is made.
 *
 * When the workflow is on, it
 * loads the issue itself — the host's copy may come from a fetch whose
 * `include` leaves the recipients out — plus the available actions
 * (next-state) and the lineage. Which actions exist, their labels and their
 * inputs are the API's; nothing here knows roles. The one rule kept here is
 * the status each action runs from (issueWorkflowActionsFor): the pick list
 * never offers an action the issue's status rules out, whatever list the API
 * returned for it.
 */
@Component({
  selector: 'app-issue-workflow-panel',
  templateUrl: './issue-workflow-panel.component.html',
  styleUrls: ['./issue-workflow-panel.component.scss'],
  standalone: false,
})
export class IssueWorkflowPanelComponent implements OnInit, OnChanges, OnDestroy {
  /** The issue the host shows, whatever fetch it came from. */
  @Input() issue: IssueWorkflowIssue | null = null;
  /** For screens that hold their own copy of the project instead of the store's. */
  @Input() project: IssueWorkflowProject | null = null;
  /**
   * The project the host knows the issue is in. Without it, an issue payload
   * that names no project is taken to be in the project of the page it is on.
   */
  @Input() projectId: string | null = null;
  /**
   * Whether the Details tab opens with the Status row. Off for a host that
   * shows the issue's status pill in its own header (the map issue sidenav),
   * so the status is shown once.
   */
  @Input() showStatus = true;
  /**
   * The issue as the API returns it on its own, once loaded: it has the
   * creator and the scope a host's copy may lack (the daily report's does).
   */
  @Output() issueLoaded = new EventEmitter<IssueWorkflowIssue>();
  /** After an action: the updated issue, for the host to refresh its own view and lists. */
  @Output() issueChanged = new EventEmitter<IssueWorkflowChange>();
  /**
   * The selected tab (0 Details, 1 Discussion). A host's own controls for the
   * issue — its Save bar — belong to Details.
   */
  @Output() tabChange = new EventEmitter<number>();
  /**
   * True while a workflow action is picked (its inputs and Submit are shown),
   * false once the pick is cleared or the action went through. The host hides
   * its own Save bar meanwhile: one way to act on the issue at a time.
   */
  @Output() actionActiveChange = new EventEmitter<boolean>();

  enabled = false;
  selectedTabIndex = 0;
  /** The issue as last returned by the API; the host's copy until then. */
  current: IssueWorkflowIssue | null = null;
  chip: IssueStatusChip | null = null;
  /** The actions on the pick list: the API's, less those the status rules out. */
  actions: WorkflowNextAction[] = [];
  history: WorkflowDiscussionEntry[] = [];
  historyFailed = false;
  loadingActions = false;
  loadingHistory = false;

  // The action area, as the punch list item detail has it: a pick list of the
  // available actions, the picked action's inputs, Submit.
  actionForm = new FormGroup({ action: new FormControl<string | null>(null) });
  actionOptions: WorkflowInputOption[] = [];
  selectedAction: WorkflowNextAction | null = null;
  /** The picked action's inputs: one control each, named after the input. */
  inputForm = new FormGroup<Record<string, FormControl>>({});
  actionFields: IssueWorkflowActionField[] = [];
  inputOptions: Record<string, WorkflowInputOption[]> = {};
  actionMediaFiles: WorkflowAttachment[] = [];
  loadingPeople = false;
  acting = false;
  /** What the API said when it refused the action (400 stage, 403 role / flag, 422 validation). */
  actionError = '';

  private storeProject: IssueWorkflowProject | null = null;
  private resolvedProject: IssueWorkflowProject | null = null;
  /** The actions next-state returned, before the status gate. */
  private offeredActions: WorkflowNextAction[] = [];
  private loadedIssueId: string | null = null;
  /** The people the picked action's pickers list, once loaded. */
  private people: IssueWorkflowPerson[] | null = null;
  /** Who the user may forward the issue to, once an action needed them. */
  private candidates: IssueWorkflowPerson[] | null = null;
  private actionActive = false;
  private requests = new Subscription();
  private inputChanges = new Subscription();
  private readonly onDestroy = new Subject<void>();
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly projectsService = inject(ProjectsService);
  private readonly commonService = inject(CommonService);
  private readonly workflowService = inject(WorkflowService);
  private readonly authService = inject(AuthService);

  // Off the workflow the element takes no box of its own: the host's content
  // is laid out exactly as if it were not wrapped.
  @HostBinding('style.display') get display(): string {
    return this.enabled ? 'block' : 'contents';
  }

  ngOnInit(): void {
    // The store holds nothing while a project fetch is in flight: the project
    // it gave stands until another one arrives, so a refetch behind the open
    // issue does not drop the panel and load it again.
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((project: IssueWorkflowProject) => {
        if (project?.id) {
          this.storeProject = project;
          this.evaluate();
        }
      });

    this.actionForm.controls.action.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(state => this.pickAction(state));

    // Files uploaded for the picked action (the attachments tile), like the
    // punch list item detail. Untagged uploads are the issue's own media.
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: WorkflowAttachment | WorkflowAttachment[]) => {
        if (!this.filesField) {
          return;
        }
        const uploaded = (Array.isArray(data) ? data : [data])
          .filter(file => file?.fileType === 'picture' || file?.fileType === 'document')
          .filter(file => (file.tags || []).length > 0)
          .map(file => ({ ...file, tags: [ACTION_ATTACHMENT_TAG] }));
        if (uploaded.length) {
          this.actionMediaFiles = [...this.actionMediaFiles, ...uploaded];
          this.publishActionMedia();
        }
      });
  }

  ngOnChanges(): void {
    this.evaluate();
  }

  get issueId(): string | null {
    return issueWorkflowId(this.issue);
  }

  get sentTo(): IssueWorkflowPerson[] {
    return this.current?.contractor_accounts || [];
  }

  /** The "Forwarded to" list: the recipients and the sub-contractor users, each person once. */
  get assignedTo(): IssueAssignee[] {
    return issueAssignees(this.current?.contractor_accounts, this.current?.sub_accounts);
  }

  get filesField(): IssueWorkflowActionField | undefined {
    return this.actionFields.find(field => field.kind === 'files');
  }

  /** Submit needs a picked action whose required inputs are filled in. */
  get canSubmit(): boolean {
    return !!this.selectedAction
      && !this.acting
      && !this.loadingPeople
      && this.inputForm.valid
      && !this.actionFields.some(field => field.kind === 'unsupported' && field.input.required);
  }

  /** Decides the project mode; loads the workflow the first time the issue is on an enabled project. */
  private evaluate(): void {
    this.resolvedProject = resolveIssueWorkflowProject(
      this.issue,
      this.project?.id ? this.project : this.storeProject,
      this.projectId || issuePageProjectId(this.router.url),
    );
    const issueId = this.issueId;
    const enabled = !!issueId && isIssueWorkflowEnabled(this.resolvedProject);
    const loadId = enabled ? issueId : null;
    this.enabled = enabled;
    if (loadId === this.loadedIssueId) {
      return;
    }

    this.loadedIssueId = loadId;
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.current = enabled ? this.issue : null;
    this.people = null;
    this.candidates = null;
    this.offeredActions = [];
    this.setActions([]);
    this.history = [];
    this.historyFailed = false;
    this.loadingActions = false;
    this.loadingHistory = false;
    this.acting = false;
    // Another issue (or none): back to Details.
    if (this.selectedTabIndex !== 0) {
      this.selectTab(0);
    }
    this.refreshView();
    if (enabled) {
      this.loadIssue();
      this.loadActions();
      this.loadHistory();
    }
  }

  private refreshView(): void {
    this.chip = this.enabled ? issueStatusChip(this.current) : null;
  }

  /** A failure leaves the host's copy on screen: the stage still shows, recipients may be missing. */
  private loadIssue(): void {
    const issueId = this.loadedIssueId;
    this.requests.add(
      this.projectsService.fetchIssue({ issueId }).subscribe({
        next: (resp: { data?: IssueWorkflowIssue }) => {
          if (resp?.data) {
            this.current = resp.data;
            this.gateActions();
            this.refreshView();
            this.issueLoaded.emit(resp.data);
          }
        },
        error: () => undefined,
      }),
    );
  }

  /** A failure, or a session that may not run workflows, offers no actions. */
  private loadActions(): void {
    const issueId = this.loadedIssueId;
    const projectId = this.resolvedProject?.id;
    if (!projectId || !this.workflowService.canExecute()) {
      this.offeredActions = [];
      this.setActions([]);
      return;
    }
    this.loadingActions = true;
    this.requests.add(
      this.projectsService.fetchIssueWorkflowActions({ issueId, projectId }).subscribe({
        next: (resp: { data?: WorkflowNextAction[] }) => {
          this.loadingActions = false;
          this.offeredActions = Array.isArray(resp?.data) ? resp.data.filter(action => !!action?.state) : [];
          this.gateActions();
          this.refreshView();
        },
        error: () => {
          this.loadingActions = false;
          this.offeredActions = [];
          this.setActions([]);
        },
      }),
    );
  }

  /** Offers the API's actions that fit the issue's current status. */
  private gateActions(): void {
    this.setActions(issueWorkflowActionsFor(this.offeredActions, this.current?.status));
  }

  /** The pick list is the API's list: its states, with its labels. */
  private setActions(actions: WorkflowNextAction[]): void {
    this.actions = actions;
    this.actionOptions = actions.map(action => ({
      value: action.state,
      label: workflowActionLabel(action),
    }));
    // The picked action stands only while it is still on offer.
    const picked = this.actionForm.controls.action.value;
    if (picked && !actions.some(action => action.state === picked)) {
      this.actionForm.controls.action.setValue(null);
    } else if (!picked && this.selectedAction) {
      this.pickAction(null);
    }
  }

  private loadHistory(): void {
    const issueId = this.loadedIssueId;
    this.loadingHistory = true;
    this.requests.add(
      this.projectsService.fetchIssueWorkflowHistory({ issueId }).subscribe({
        next: (resp: { data?: WorkflowLineageStep[] }) => {
          this.loadingHistory = false;
          this.historyFailed = false;
          this.history = issueWorkflowHistory(Array.isArray(resp?.data) ? resp.data : []);
        },
        error: () => {
          this.loadingHistory = false;
          this.historyFailed = true;
        },
      }),
    );
  }

  selectTab(index: number): void {
    this.selectedTabIndex = index;
    this.tabChange.emit(index);
  }

  retryHistory(): void {
    if (this.loadedIssueId) {
      this.historyFailed = false;
      this.loadHistory();
    }
  }

  personLabel(person: IssueWorkflowPerson): string {
    return workflowPersonLabel(person);
  }

  /**
   * An action was picked (or the pick cleared): its inputs become the inline
   * form. The people pickers need the user's candidates, fetched the first
   * time an action asks for them (see loadCandidates).
   */
  private pickAction(state: string | null): void {
    this.selectedAction = this.actions.find(action => action.state === state) || null;
    this.setActionActive(!!this.selectedAction);
    this.actionError = '';
    this.loadingPeople = false;
    this.actionMediaFiles = [];
    this.people = null;
    this.buildActionFields();
    if (this.filesField) {
      this.publishActionMedia();
    }

    const formInputs = this.selectedAction?.options?.formInputs || [];
    if (!formInputs.some(input => this.isPeopleInput(input.name))) {
      return;
    }
    const picked = this.selectedAction;
    this.loadingPeople = true;
    this.requests.add(
      this.loadCandidates().subscribe({
        next: people => {
          if (this.selectedAction === picked) {
            this.people = people;
            this.loadingPeople = false;
            this.applyPeople();
          }
        },
        error: (err: ApiError) => {
          if (this.selectedAction === picked) {
            this.loadingPeople = false;
            this.actionError = err?.error?.error || 'The people list could not be loaded. Pick the action again to retry.';
          }
        },
      }),
    );
  }

  private setActionActive(active: boolean): void {
    if (active !== this.actionActive) {
      this.actionActive = active;
      this.actionActiveChange.emit(active);
    }
  }

  /**
   * Who the user may forward the issue to: GET
   * /v1/issue/:id/contractor-users answers for the caller (a project manager
   * or a non-contractor recipient gets everyone on the project, a contractor
   * the contractor users), so nothing here filters by role. The current user
   * is left out of the options, see refreshInputOptions.
   */
  private loadCandidates(): Observable<IssueWorkflowPerson[]> {
    if (this.candidates) {
      return of(this.candidates);
    }
    return this.projectsService.fetchIssueContractorUsers({ issueId: this.loadedIssueId }).pipe(
      map((resp: { data?: IssueWorkflowPerson[] }) => (Array.isArray(resp?.data) ? resp.data : [])),
      tap(people => (this.candidates = people)),
    );
  }

  private isPeopleInput(name: string): boolean {
    return name === USERS_INPUT || name === SUB_CONTRACTOR_INPUT;
  }

  /**
   * The people are in: the people pickers get their options. Every picker
   * starts empty: a send (Forward) again adds people to the recipients, and
   * restating them would email them again.
   */
  private applyPeople(): void {
    this.refreshInputOptions();
  }

  /** A control per input of the picked action, by the input's type and the options on hand. */
  private buildActionFields(): void {
    this.inputChanges.unsubscribe();
    this.inputChanges = new Subscription();
    this.inputOptions = {};

    const formInputs = this.selectedAction?.options?.formInputs || [];
    const controls: Record<string, FormControl> = {};
    this.actionFields = formInputs.map(input => {
      const kind = workflowInputKind(input, this.isPeopleInput(input.name));
      if (kind !== 'files' && kind !== 'unsupported') {
        let value: unknown = null;
        if (kind === 'multi-choice') {
          value = [];
        } else if (kind === 'text') {
          value = '';
        }
        controls[input.name] = new FormControl(value, input.required ? [Validators.required] : []);
      }
      return { input, kind, label: workflowInputLabel(this.selectedAction, input) };
    });
    this.inputForm = new FormGroup<Record<string, FormControl>>(controls);

    this.refreshInputOptions();
    this.inputChanges.add(this.inputForm.valueChanges.subscribe(() => {
      this.actionError = '';
      this.refreshInputOptions();
    }));
  }

  /** The options of every picker, dropping the picked values that are no longer among them. */
  private refreshInputOptions(): void {
    // Forwarding to yourself is a no-op: you are never on the list.
    const currentUserId = this.authService.getCurrentUser()?.id;
    const subContractorId = this.inputForm.controls[SUB_CONTRACTOR_INPUT]?.value as string;
    const peopleOptions: Record<string, WorkflowInputOption[]> = {
      [SUB_CONTRACTOR_INPUT]: workflowSubContractorOptions(this.people, currentUserId),
      [USERS_INPUT]: workflowPersonOptions(workflowPeopleOfSubContractor(this.people, subContractorId), currentUserId),
    };

    this.actionFields.forEach(({ input, kind }) => {
      if (kind !== 'choice' && kind !== 'multi-choice') {
        return;
      }
      const next = this.isPeopleInput(input.name) ? peopleOptions[input.name] : workflowInputOptions(input);
      const previous = this.inputOptions[input.name];
      // app-dropdown rebuilds its list on a new array: keep the reference while the list is the same.
      if (!previous || JSON.stringify(previous) !== JSON.stringify(next)) {
        this.inputOptions[input.name] = next;
      }
      // Nothing to drop while the list is still on its way.
      if (this.isPeopleInput(input.name) && !this.people) {
        return;
      }
      const control = this.inputForm.controls[input.name];
      const allowed = next.map(option => option.value);
      if (kind === 'multi-choice') {
        const picked: unknown[] = Array.isArray(control.value) ? control.value : [];
        const kept = picked.filter(value => allowed.includes(String(value)));
        if (kept.length !== picked.length) {
          control.setValue(kept, { emitEvent: false });
        }
      } else if (control.value !== null && control.value !== '' && !allowed.includes(String(control.value))) {
        control.setValue(null, { emitEvent: false });
      }
    });
  }

  /** app-pictures-section-with-tags shows what this subject carries, not its input. */
  private publishActionMedia(): void {
    const files = this.actionMediaFiles;
    setTimeout(() => this.projectsService.mediaData.next(files));
  }

  deleteActionPicture(picture: WorkflowAttachment): void {
    this.actionMediaFiles = this.actionMediaFiles.filter(file => file.id !== picture?.id);
    this.publishActionMedia();
  }

  /** Runs the picked action with the values of its own inputs. */
  executeAction(): void {
    const action = this.selectedAction;
    if (!action || !this.canSubmit) {
      return;
    }
    const values: Record<string, unknown> = { ...this.inputForm.getRawValue() };
    const filesField = this.filesField;
    if (filesField) {
      values[filesField.input.name] = this.actionMediaFiles.map(file => file.id);
    }
    const formValues = buildWorkflowFormValues(
      action.options?.formInputs,
      values,
      name => this.isPeopleInput(name),
    );

    this.acting = true;
    this.actionError = '';
    this.requests.add(
      this.projectsService.issueWorkflowAction({ issueId: this.loadedIssueId, state: action.state, formValues })
        .subscribe({
          next: (resp: { data?: IssueWorkflowIssue; message?: string }) => this.afterAction(action, resp),
          error: (err: ApiError) => {
            this.acting = false;
            this.actionError = err?.error?.error || err?.error?.message || 'Action failed';
          },
        }),
    );
  }

  /**
   * The response is the updated issue; the actions and the history are
   * fetched again. The user stays on Details with the pick list cleared.
   */
  private afterAction(action: WorkflowNextAction, resp: { data?: IssueWorkflowIssue; message?: string }): void {
    this.acting = false;
    this.actionForm.reset({ action: null });
    this.commonService.notification(resp?.message || 'Action completed', 'success');
    if (resp?.data) {
      this.current = resp.data;
      // Until next-state answers, nothing the new status rules out stays on offer.
      this.gateActions();
    } else {
      this.loadIssue();
    }
    this.refreshView();
    this.loadActions();
    this.loadHistory();
    this.issueChanged.emit({ issue: this.current, state: action.state });
  }

  ngOnDestroy(): void {
    this.requests.unsubscribe();
    this.inputChanges.unsubscribe();
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
